import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { listSavedExplanations, removeSavedExplanation, SavedExplanation } from "../services/savedExplanationService";

const SavedExplanationsPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [items, setItems] = useState<SavedExplanation[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await listSavedExplanations();
      setItems(data || []);
    } catch (err) {
      console.error("Erro ao carregar explicações salvas", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    try {
      await removeSavedExplanation(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      console.error("Erro ao remover explicação", err);
    } finally {
      setRemovingId(null);
    }
  };

  const formatDate = (ts: any) => {
    if (!ts) return "";
    try {
      if (ts.seconds) {
        return new Date(ts.seconds * 1000).toLocaleDateString();
      }
      return new Date(ts).toLocaleDateString();
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-emerald-50">
      <header className="bg-white border-b border-gray-100 shadow-sm px-4 py-4 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-emerald-50 transition"
            >
              ← Voltar
            </button>
            <div>
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-[0.12em]">Professor Pobre</p>
              <h1 className="text-2xl font-black text-gray-900">Caderno de Explicações</h1>
              <p className="text-sm text-gray-600">Guarde e revise as melhores explicações</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="bg-white rounded-2xl shadow p-6 text-gray-600">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow p-6 text-gray-600">
            Nenhuma explicação salva ainda. Salve uma explicação no Professor Pobre e ela aparece aqui.
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl shadow border border-gray-100 p-5 flex flex-col gap-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2 text-sm text-gray-600 font-semibold">
                  {item.subject && <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">{item.subject}</span>}
                  {item.topic && <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full border border-purple-100">{item.topic}</span>}
                  {item.source && <span className="px-2 py-1 bg-gray-50 text-gray-600 rounded-full border border-gray-100 capitalize">{item.source}</span>}
                </div>
                <div className="text-xs text-gray-400">{formatDate(item.createdAt)}</div>
              </div>

              {item.statement && (
                <p className="text-gray-800 text-sm leading-relaxed line-clamp-3 whitespace-pre-wrap">
                  {item.statement}
                </p>
              )}

              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-800">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  components={{
                    p: (props) => <p className="mb-2 leading-relaxed" {...props} />,
                    strong: (props) => <strong className="font-semibold" {...props} />,
                    em: (props) => <em className="italic" {...props} />,
                    ul: (props) => <ul className="list-disc pl-5 mb-2" {...props} />,
                    ol: (props) => <ol className="list-decimal pl-5 mb-2" {...props} />,
                    li: (props) => <li className="mb-1" {...props} />,
                    br: () => <br />,
                  }}
                >
                  {item.explanation}
                </ReactMarkdown>
              </div>

              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => handleRemove(item.id)}
                  disabled={removingId === item.id}
                  className="text-sm text-red-600 hover:text-red-700 font-semibold px-3 py-2 rounded-lg hover:bg-red-50"
                >
                  {removingId === item.id ? "Removendo..." : "Remover"}
                </button>
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
};

export default SavedExplanationsPage;
